import { createAsyncThunk } from '@reduxjs/toolkit';

import { fetchWrapper } from '../../../../helpers';
import { TREATMENT_PLANS_V2_API_BASE } from '../../constants';

import type {
  ITreatmentPlanV3MedicalDiagnosis,
  ITreatmentPlanV3MedicalInfo,
  ITreatmentPlanV3MedicalPage,
  ITreatmentPlanV3MedicalSelection,
  CreateTreatmentPlanV3MedicalDiagnosisPayload,
  CreateTreatmentPlanV3MedicalSelectionPayload,
  DeleteTreatmentPlanV3MedicalDiagnosisPayload,
  DeleteTreatmentPlanV3MedicalSelectionPayload,
  GetTreatmentPlanV3MedicalPagePayload,
  UpdateTreatmentPlanV3MedicalDiagnosisPayload,
  UpdateTreatmentPlanV3MedicalInfoPayload,
} from './types';

export function createTreatmentPlanV3MedicalThunks() {
  return {
    getTreatmentPlanV3MedicalPage: getTreatmentPlanV3MedicalPage(),
    updateTreatmentPlanV3MedicalInfo: updateTreatmentPlanV3MedicalInfo(),
    createTreatmentPlanV3MedicalDiagnosis: createTreatmentPlanV3MedicalDiagnosis(),
    updateTreatmentPlanV3MedicalDiagnosis: updateTreatmentPlanV3MedicalDiagnosis(),
    deleteTreatmentPlanV3MedicalDiagnosis: deleteTreatmentPlanV3MedicalDiagnosis(),
    createTreatmentPlanV3MedicalSelection: createTreatmentPlanV3MedicalSelection(),
    deleteTreatmentPlanV3MedicalSelection: deleteTreatmentPlanV3MedicalSelection(),
  };
}

function getTreatmentPlanV3MedicalPage() {
  return createAsyncThunk<ITreatmentPlanV3MedicalPage, GetTreatmentPlanV3MedicalPagePayload>(
    'treatmentPlanV3/getTreatmentPlanV3MedicalPage',
    async ({ treatmentPlanId, getAccessTokenSilently }) =>
      fetchWrapper.get({
        url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/medical/`,
        getAccessTokenSilently,
      }),
  );
}

function updateTreatmentPlanV3MedicalInfo() {
  return createAsyncThunk<
    ITreatmentPlanV3MedicalInfo | null,
    UpdateTreatmentPlanV3MedicalInfoPayload
  >(
    'treatmentPlanV3/updateTreatmentPlanV3MedicalInfo',
    async ({ treatmentPlanId, data, getAccessTokenSilently }) =>
      fetchWrapper.patch(
        {
          url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/medical/`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function createTreatmentPlanV3MedicalDiagnosis() {
  return createAsyncThunk<
    ITreatmentPlanV3MedicalDiagnosis,
    CreateTreatmentPlanV3MedicalDiagnosisPayload
  >(
    'treatmentPlanV3/createTreatmentPlanV3MedicalDiagnosis',
    async ({ treatmentPlanId, data, getAccessTokenSilently }) =>
      fetchWrapper.post(
        {
          url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/medical/diagnoses/`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function updateTreatmentPlanV3MedicalDiagnosis() {
  return createAsyncThunk<
    ITreatmentPlanV3MedicalDiagnosis,
    UpdateTreatmentPlanV3MedicalDiagnosisPayload
  >(
    'treatmentPlanV3/updateTreatmentPlanV3MedicalDiagnosis',
    async ({ treatmentPlanId, diagnosisId, data, getAccessTokenSilently }) =>
      fetchWrapper.patch(
        {
          url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/medical/diagnoses/${diagnosisId}/`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function deleteTreatmentPlanV3MedicalDiagnosis() {
  return createAsyncThunk<void, DeleteTreatmentPlanV3MedicalDiagnosisPayload>(
    'treatmentPlanV3/deleteTreatmentPlanV3MedicalDiagnosis',
    async ({ treatmentPlanId, diagnosisId, getAccessTokenSilently }) => {
      await fetchWrapper.delete({
        url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/medical/diagnoses/${diagnosisId}/`,
        getAccessTokenSilently,
      });
    },
  );
}

function createTreatmentPlanV3MedicalSelection() {
  return createAsyncThunk<
    ITreatmentPlanV3MedicalSelection,
    CreateTreatmentPlanV3MedicalSelectionPayload
  >(
    'treatmentPlanV3/createTreatmentPlanV3MedicalSelection',
    async ({ treatmentPlanId, data, getAccessTokenSilently }) =>
      fetchWrapper.post(
        {
          url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/medical/selections/`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function deleteTreatmentPlanV3MedicalSelection() {
  return createAsyncThunk<void, DeleteTreatmentPlanV3MedicalSelectionPayload>(
    'treatmentPlanV3/deleteTreatmentPlanV3MedicalSelection',
    async ({ treatmentPlanId, selectionType, selectionId, getAccessTokenSilently }) => {
      await fetchWrapper.delete({
        url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/medical/selections/${selectionType}/${selectionId}/`,
        getAccessTokenSilently,
      });
    },
  );
}
